// Admin API types + deriveAccountState. (Fake-server mocks removed; real client lives in ../adminApi.real.ts.)
export type Role = 'admin' | 'advisor' | 'wrise'   // roles the api stores and assigns (no member/root)
/** Roles the Users list can filter by; team_leader is list-only. */
export type ListRole = Role | 'team_leader'
export const LIST_ROLES: ListRole[] = ['admin', 'wrise', 'team_leader', 'advisor']
export const isListRole = (v: unknown): v is ListRole => LIST_ROLES.includes(v as ListRole)
export interface AdvisorProfile {
  idCardNumber: string; legalName: string
  /** Persisted BANK CODE (e.g. 'KBANK') — resolve to display name via banks.ts */
  bankName: string; bankAccountName: string; bankAccountNumber: string
  phone: string; lineId: string; address: string; postalCode: string
  contractStatus: 'signed' | 'awaiting'; contractUrl: string | null
}
export interface MockUser {
  id: string; name: string; email: string
  roles: Role[]; isActive: boolean
  invitationSentAt: string | null; passwordSetAt: string | null
  /** When the setup invitation link expires (drives Invite Expired state) */
  inviteExpiresAt: string | null
  createdAt: string
  lastLoginAt: string | null
  activeSessions: number
  /** Set when isActive=false — drives the Disabled one-liner. */
  deactivatedAt?: string | null
  deactivatedBy?: string | null
  advisorProfile?: AdvisorProfile | null
  caseCount?: number
}

/** Account lifecycle state machine (page-plan §5e). */
export type AccountState = 'invited' | 'invite_expired' | 'active' | 'disabled'

/**
 * Derive the account lifecycle state from mock fields.
 * Precedence: disabled (admin off-switch) → active (password set) →
 * invite_expired (link past its expiry) → invited.
 */
export function deriveAccountState(
  user: Pick<MockUser, 'isActive' | 'passwordSetAt' | 'inviteExpiresAt'>,
  now: Date = new Date(),
): AccountState {
  if (!user.isActive) return 'disabled'
  if (user.passwordSetAt) return 'active'
  if (user.inviteExpiresAt && new Date(user.inviteExpiresAt).getTime() < now.getTime()) {
    return 'invite_expired'
  }
  return 'invited'
}
/** Audit action category — drives the full-log action-type filter (page-plan §5f). */
export type AuditType =
  | 'created' | 'invited' | 'password' | 'role' | 'profile' | 'bank'
  | 'session' | 'login' | 'email' | 'status'
/** A Users list row: MockUser whose roles may include the mock-only team_leader. */
export type UserListRow = Omit<MockUser, 'roles'> & { roles: ListRole[] }
export interface AuditEvent {
  id: string; label: string; date: string; actor: string
  /** Optional (legacy fixtures omit it) — category for the full-log filter. */
  type?: AuditType
  /** Optional one-line detail shown in the full-log "Details" column. */
  details?: string
}
export type RegStatus = 'pending' | 'approved' | 'rejected'
export interface MockRegistration {
  id: string; profileId: string | null
  fullName: string; idCardNumber: string; email: string; phone: string; lineId: string
  birthDate: string; address: string; postalCode: string; referrer: string | null
  status: RegStatus; invitationSentAt: string | null; passwordSetAt: string | null
  /** Set when an approved advisor is later revoked (→ "Revoked"); null when reject is a plain decline. */
  revokedAt: string | null
  createdAt: string
}
/** Editable field subset for admin-initiated create / pending-edit. */
export interface RegistrationInput {
  fullName: string; idCardNumber: string; email: string; phone: string; lineId: string
  birthDate: string; address: string; postalCode: string; referrer: string | null
  /** Create only: the team the new advisor joins (web#78). */
  teamId?: string
}
/** Typed error so mocks can simulate HTTP error codes (e.g. 409 ALREADY_APPROVED). */
export class MockApiError extends Error {
  code: string
  constructor(code: string, message?: string) {
    super(message ?? code)
    this.name = 'MockApiError'
    this.code = code
  }
}
export interface ListParams {
  search?: string; role?: ListRole; status?: string
  sortBy?: string; sortDir?: 'asc' | 'desc'; page: number; pageSize: number
}
